/* The queue's pure logic (designs/intake-ui-parity.md UP9, cut to one form
   by intake-unified-form.md UF5/UF6) — the prototype's Dashboard/Rollup/
   export/delivery code ported onto Hermes's board + canonical-teams reads.
   Everything filterable, sortable, or summable lives HERE, fully
   unit-tested; the components stay presentational. */

import {
  DISPOSITIONS,
  EFFORTS,
  HEALTHS,
  JAMA_STATUSES,
  PHASE_TARGETS,
  PLATFORM_STATUSES,
  PRIORITIES,
  SCOPES,
  SUB_STATUSES,
  labelFor,
  type CapabilityBoardRow,
  type CapabilityTracking,
  type PersonRef,
  type VocabOption,
} from "../../../shared/capabilities";
import type { CanonicalTeam, TeamKind } from "../../../shared/canonical-teams";
import type { Product, Vehicle } from "../../../shared/products";
import type { Stamp } from "../../../shared/organizer-roles";

/* ── the joined row every queue surface reads (UP4) ── */

export interface BoardView {
  id: string;
  name: string;
  description: string | null;
  teamId: string;
  teamName: string;
  /* the registering team's kind — null = not recorded (TP1) */
  teamKind: TeamKind | null;
  /* the effort's product's market, else the registering team's product's
     (teams-and-products.md TP2); null = not recorded */
  market: string | null;
  /* the lane lead (UF16); null on rows filed before the field — not recorded */
  pointOfContact: PersonRef | null;
  /* the product / vehicle it is for (UF21); null = not recorded */
  productId: string | null;
  /* "Product › vehicle" when a vehicle is named, the product alone otherwise */
  productName: string | null;
  vehicleId: string | null;
  scope: string;
  subStatus: string;
  /* groom-side (UF13); null = not yet prioritised */
  priority: string | null;
  neededBy: string | null;
  phaseTarget: string | null;
  jamaStatus: string | null;
  jamaLink: string | null;
  dependencies: string | null;
  /* which teams are expected to deliver it (intake-ownership-model.md OM7).
     null = not recorded, [] = discussed but not settled, [ids] = recorded.
     Never inferred from the submitting team. */
  owners: string[] | null;
  /* the same answer resolved to display names, once, here — the chip, the
     drawer and the CSV all need it and none should re-derive it row by row.
     An unknown slug keeps its id rather than rendering blank. */
  ownerNames: string[] | null;
  archived: boolean;
  stakeholderCount: number;
  score: number;
  tracking: CapabilityTracking | null;
  deliveryDone: number;
  recorded: Stamp;
}

/* the delivery chain (UP4): the prototype's six stages plus Charter
   leading them (IDM9 — the operating model's intake → charter → delivery
   order) */
export const DELIVERY_STAGES = [
  { key: "dcCharter", label: "Charter" },
  { key: "dcReqDrafted", label: "Requirement drafted" },
  { key: "dcReqLinked", label: "Linked in Jama" },
  { key: "dcCode", label: "Code" },
  { key: "dcTestCases", label: "Test cases" },
  { key: "dcTestArtifacts", label: "Test artifacts" },
  { key: "dcVerified", label: "Verified" },
] as const;
export const DELIVERY_TOTAL = DELIVERY_STAGES.length;

export const deliveryDone = (t: CapabilityTracking | null): number =>
  t === null ? 0 : DELIVERY_STAGES.reduce((n, s) => n + (t[s.key] ? 1 : 0), 0);

export function toView(
  rows: CapabilityBoardRow[],
  teams: CanonicalTeam[],
  products: Product[] = [],
  vehicles: Vehicle[] = [],
): BoardView[] {
  const teamById = new Map(teams.map((t) => [t.id, t]));
  const productById = new Map(products.map((p) => [p.id, p]));
  /* the registering team's market, when it owns exactly ONE live product —
     with several (option B) there is no single market to fall back to */
  const liveByTeam = new Map<string, Product[]>();
  for (const p of products)
    if (p.teamId != null && !p.retired) liveByTeam.set(p.teamId, [...(liveByTeam.get(p.teamId) ?? []), p]);
  const productByTeam = new Map(
    [...liveByTeam].filter(([, ps]) => ps.length === 1).map(([id, ps]) => [id, ps[0]!]),
  );
  const vehicleById = new Map(vehicles.map((v) => [v.id, v]));
  const productLabel = (productId: string, vehicleId: string | null): string => {
    const name = productById.get(productId)?.name ?? productId;
    if (vehicleId === null) return name;
    return `${name} › ${vehicleById.get(vehicleId)?.name ?? vehicleId}`;
  };
  return rows.map(({ capability: c, stakeholderCount, tracking, score }) => ({
    id: c.id,
    name: c.name,
    description: c.description,
    teamId: c.teamId,
    /* an unknown slug still renders honestly rather than blank */
    teamName: teamById.get(c.teamId)?.name ?? c.teamId,
    teamKind: teamById.get(c.teamId)?.kind ?? null,
    /* an effort that names a product takes THAT product's market, even when
       it is not recorded — only an effort with no product falls back to the
       registering team's (Copilot on #147) */
    market:
      c.productId != null
        ? (productById.get(c.productId)?.market ?? null)
        : (productByTeam.get(c.teamId)?.market ?? null),
    pointOfContact: c.pointOfContact ?? null,
    productId: c.productId ?? null,
    /* an unknown slug still renders honestly rather than blank */
    productName: c.productId == null ? null : productLabel(c.productId, c.vehicleId ?? null),
    vehicleId: c.vehicleId ?? null,
    scope: c.scope,
    subStatus: c.subStatus,
    priority: c.priority,
    neededBy: c.neededBy,
    phaseTarget: c.phaseTarget,
    jamaStatus: c.jamaStatus,
    jamaLink: c.jamaLink,
    dependencies: c.dependencies,
    owners: c.owners ?? null,
    ownerNames: c.owners == null ? null : c.owners.map((id) => teamById.get(id)?.name ?? id),
    archived: c.archived === true,
    stakeholderCount,
    score,
    tracking,
    deliveryDone: deliveryDone(tracking),
    recorded: c.recorded,
  }));
}

/* ── routing (UF6): groomed AND owned, or nobody owns it yet ──

   A requester's owners are an EXPECTATION (OM2). Reading them as the owner
   group would let a requester route their own ask, which is authority nobody
   granted — so a row is routed only once an admin has groomed it (a tracking
   record exists) and it names at least one owner. */

export const UNOWNED_LABEL = "Nobody owns this yet";

/* "groomed" is the groomed FLAG — the one test the Board view's lanes and
   the Groomed column use — not "a tracking record exists" (intake-flow-
   review.md FR2) */
export const isRouted = (r: BoardView): boolean =>
  r.tracking?.groomed === true && r.owners !== null && r.owners.length > 0;

/* ── the Board view (intake-groom.md IG9–IG11) ── */

/* what a card still lacks (IG9) — the honest-state red of settled
   decision #2: a gap is named, never guessed or filled */
export const groomGaps = (r: BoardView): string[] => {
  const gaps: string[] = [];
  if (r.owners === null || r.owners.length === 0) gaps.push("no owner");
  if (r.priority === null) gaps.push("no priority");
  if (r.tracking?.effort == null) gaps.push("not sized");
  if (r.tracking?.disposition == null) gaps.push("no outcome");
  return gaps;
};

/* the lanes (IG3, IG10): every effort nobody has groomed waits in Not
   groomed — the queue's own Ungroomed test — whatever its status; the
   lifecycle lanes hold groomed efforts by status, closed statuses folded */
const isGroomed = (r: BoardView): boolean => r.tracking?.groomed === true;
const byStatus =
  (...statuses: (string | null)[]) =>
  (r: BoardView): boolean =>
    isGroomed(r) && statuses.includes(r.tracking?.platformStatus ?? null);
export const BOARD_LANES: readonly { label: string; holds: (r: BoardView) => boolean }[] = [
  { label: "Not groomed", holds: (r) => !isGroomed(r) },
  { label: "Triage", holds: byStatus(null, "triage") },
  { label: "Accepted", holds: byStatus("accepted") },
  { label: "In progress", holds: byStatus("in_progress") },
  { label: "On hold / Blocked", holds: byStatus("on_hold", "blocked") },
  { label: "Delivered", holds: byStatus("delivered") },
  { label: "Closed", holds: byStatus("rejected", "duplicate") },
];

/* the rows as the Board view reads them, lane by lane — keeping the
   queue's sort inside each lane — which is the order ↑/↓ step (IG11) */
export const boardLanes = (rows: BoardView[]): BoardView[][] =>
  BOARD_LANES.map((lane) => rows.filter(lane.holds));
export const boardOrder = (rows: BoardView[]): BoardView[] => boardLanes(rows).flat();

/* the group a row sits under on the resting queue: its first owner's name
   once routed, else the one unowned group */
export const ownerGroup = (r: BoardView): string =>
  isRouted(r) ? r.ownerNames![0]! : UNOWNED_LABEL;

/* the three honest states of ownership as ONE sentence-maker (OM2), so the
   drawer, the row chip and the CSV can never word them differently:
     null → not recorded      [] → discussed, not settled      [names] → list */
export const ownersText = (names: string[] | null): string =>
  names === null ? "" : names.length === 0 ? "Not yet decided" : names.join(", ");

export const ownersLabel = (names: string[] | null): string =>
  names === null ? "Not recorded" : ownersText(names);

/* the compact owner list — one name plus an overflow count, because a row
   that lists five teams stops being scannable */
const compact = (names: string[]): string =>
  names.length === 1 ? names[0]! : `${names[0]!} +${names.length - 1}`;

/* the queue row's owner cell (UF6), in the honest-states vocabulary:
     unowned  → "Nobody owns this yet"  (red — not recorded)
     suggested → "Suggested: X"          (orange — a requester's expectation,
                                          not yet groomed)
     routed   → "X" / "X +2"            (plain) */
export type OwnerChip = { kind: "unowned" | "suggested" | "routed"; text: string };

export const ownersChip = (r: Pick<BoardView, "owners" | "ownerNames" | "tracking">): OwnerChip => {
  const names = r.ownerNames;
  if (names === null || names.length === 0) return { kind: "unowned", text: UNOWNED_LABEL };
  if (r.tracking === null) return { kind: "suggested", text: `Suggested: ${compact(names)}` };
  return { kind: "routed", text: compact(names) };
};

/* ── the prototype's predicates, verbatim ── */

export const isShared = (r: BoardView): boolean => r.scope === "common" || r.stakeholderCount > 0;
/* null = no phase recorded anywhere — honest, not "future" */
export const effectivePhase = (r: BoardView): string | null =>
  r.tracking?.roadmapPhase ?? r.phaseTarget;

/* ── filters (UP4) ── */

export interface BoardFilters {
  q: string;
  team: string;
  /* the product / vehicle (UF21); "" = every row */
  product: string;
  /* the owning team — the routing lens (UF5). Matches a row whose owners
     name the team, routed or suggested; "" = every row. */
  owner: string;
  status: string;
  phase: string;
  sharedOnly: boolean;
  unownedOnly: boolean;
  committedOnly: boolean;
  showArchived: boolean;
  /* FR4: only the reader's own efforts */
  mineOnly: boolean;
}

export const EMPTY_FILTERS: BoardFilters = {
  q: "",
  team: "",
  product: "",
  owner: "",
  status: "",
  phase: "",
  sharedOnly: false,
  unownedOnly: false,
  committedOnly: false,
  showArchived: false,
  mineOnly: false,
};

export const hasFilters = (f: BoardFilters): boolean =>
  f.q !== "" ||
  f.team !== "" ||
  f.product !== "" ||
  f.owner !== "" ||
  f.status !== "" ||
  f.phase !== "" ||
  f.sharedOnly ||
  f.unownedOnly ||
  f.committedOnly ||
  f.showArchived ||
  f.mineOnly;

/* the reader's own efforts (intake-flow-review.md FR4): the first revision
   was stamped by them — from the filings read, which carries each origin
   stamp — or they are the recorded point of contact. Without the filings
   read only the contact half is known, and the caller says so. */
export function myEffortIds(
  rows: BoardView[],
  filings: readonly { capabilityId: string; recorded?: { by?: { userId: string } } }[] | null,
  /* every id that is this reader — the object id, plus the email older
     stamps carry (one-identity.md OI6/OI9) — compared without case, as
     isMine does */
  readerIds: readonly string[],
): { ids: Set<string>; filingsKnown: boolean } {
  const ids = new Set<string>();
  const me = new Set(readerIds.map((i) => i.toLowerCase()));
  const isMe = (stamp: string | undefined): boolean => stamp !== undefined && me.has(stamp.toLowerCase());
  for (const f of filings ?? []) if (isMe(f.recorded?.by?.userId)) ids.add(f.capabilityId);
  for (const r of rows) if (isMe(r.pointOfContact?.userId)) ids.add(r.id);
  return { ids, filingsKnown: filings !== null };
}

export function filterRows(
  rows: BoardView[],
  f: BoardFilters,
  mine: ReadonlySet<string> = new Set(),
): BoardView[] {
  return rows.filter((r) => {
    /* archived rows exist only behind the checkbox — the prototype's rule */
    if (!f.showArchived && r.archived) return false;
    if (f.q && !r.name.toLowerCase().includes(f.q.toLowerCase())) return false;
    if (f.team && r.teamId !== f.team) return false;
    if (f.product && r.productId !== f.product) return false;
    if (f.owner && !(r.owners ?? []).includes(f.owner)) return false;
    if (f.status && (r.tracking?.platformStatus ?? "") !== f.status) return false;
    if (f.phase && effectivePhase(r) !== f.phase) return false;
    if (f.sharedOnly && !isShared(r)) return false;
    if (f.unownedOnly && isRouted(r)) return false;
    if (f.committedOnly && !(r.tracking?.committed ?? false)) return false;
    if (f.mineOnly && !mine.has(r.id)) return false;
    return true;
  });
}

/* ── sorting: the prototype's option-index semantics, verbatim ── */

const healthRank = (h: string | null | undefined): number =>
  h === "red" ? 0 : h === "amber" ? 1 : h === "green" ? 2 : 3;

const optIdx = (opts: ReadonlyArray<VocabOption>, v: string | null | undefined): number => {
  const i = opts.findIndex((o) => o.value === v);
  return i < 0 ? 999 : i;
};

export type SortKey =
  | "owner"
  | "name"
  | "team"
  | "product"
  | "contact"
  | "shared"
  | "score"
  | "priority"
  | "status"
  | "health"
  | "phase"
  | "delivery"
  | "groomed";

export function sortValue(r: BoardView, key: SortKey): string | number {
  switch (key) {
    case "owner":
      /* the resting order (UF5): the unowned group first — it is what nobody
         has picked up — then owner groups alphabetically. Rows arrive
         score-ranked and Array.sort is stable, so each group keeps its own
         score order inside it. The leading marker sorts unowned ahead of any
         team name without a second comparator. */
      return isRouted(r) ? `1 ${ownerGroup(r).toLowerCase()}` : "0";
    case "name":
      return r.name.toLowerCase();
    case "team":
      return r.teamName.toLowerCase();
    case "product":
      return r.productName === null ? "1" : `0 ${r.productName.toLowerCase()}`;
    case "contact":
      /* not recorded sorts last, like every other unknown — a digit marker,
         because "~" collates BEFORE letters under localeCompare */
      return r.pointOfContact === null ? "1" : `0 ${r.pointOfContact.name.toLowerCase()}`;
    case "shared":
      return isShared(r) ? 0 : 1;
    case "priority":
      return optIdx(PRIORITIES, r.priority);
    case "status":
      return optIdx(PLATFORM_STATUSES, r.tracking?.platformStatus);
    case "phase":
      return optIdx(PHASE_TARGETS, effectivePhase(r));
    case "score":
      return r.score;
    case "delivery":
      return r.deliveryDone;
    case "health":
      return healthRank(r.tracking?.health);
    case "groomed":
      return (r.tracking?.groomed ?? false) ? 0 : 1;
  }
}

export function sortRows(rows: BoardView[], key: SortKey | null, dir: "asc" | "desc"): BoardView[] {
  if (key === null) return rows;
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}

/* ── the tooltip layer (UP8) — the prototype's help strings, verbatim where
   they survive; one queue, so one set. intake-queue-help.md QH2: the
   visible words say "effort" and "groomed", never "ask", "capability" or
   "triaged" ── */

export const COL_HELP: Record<SortKey, string> = {
  owner:
    "Which team(s) are expected to DELIVER this effort. Plain = an intake manager groomed it and named the owner — that is what routes an effort. 'Suggested' (orange) = the requester's expectation, not yet groomed. 'Nobody owns this yet' (red) = not routed. Never inferred from the requesting team.",
  name: "The effort. Tags: shared, epic (has a Jira epic), committed, archived.",
  team: "The team that registered the effort — a platform, product or other team. A team added on the spot waits for an Admin to file it on Teams.",
  product:
    "The product the effort is for, and which of its vehicles when one is named (Product › vehicle). A product belongs to a team (a team may own several). Blank = not recorded.",
  contact:
    "The lane lead running this effort — who platform calls when another team needs the same thing. Red = not recorded (rows filed before the field existed).",
  shared: "Needed by more than one team (common scope or has +1 teams) — a build-once candidate.",
  score:
    "Priority score = value × reach ÷ effort. Higher = do sooner. Value from the priority set at grooming (Must/Should/Could), reach = how many teams it serves (shared items rank higher), effort = platform cost (S/M/L/XL, admin-set; unset assumes M). Scores rank within an owning team, not across: two teams' efforts are worked by different engineers.",
  priority: "The MoSCoW priority (Must / Should / Could / Won't) an intake manager sets when grooming. Blank until then — the requester is not asked for it.",
  status:
    "Platform status (Triage → Accepted → In progress → … → Delivered / Rejected). Falls back to the requester's status until groomed.",
  health:
    "RAG health for exec reporting — Red = off track, Amber = at risk, Green = on track. Admin-set.",
  phase:
    "Roadmap phase the platform is targeting. 'req:' shows the team's originally requested phase when the admin has moved it.",
  delivery:
    "Delivery chain progress out of 7: Charter → Requirement → Linked in Jama → Code → Test cases → Test artifacts → Verified.",
  groomed:
    "Whether an intake manager has groomed this — set by any change made with Groom… in the effort. Ungroomed = still needs an intake manager's attention.",
};

/* the stat cards — every card explains itself (QH1); the unowned card says
   it counts suggestions too, because isRouted needs a groom (QH3) */
export const STAT_HELP = {
  teams:
    "Teams on Hermes's controlled team list, retired ones not counted. Admins curate the list on the Teams section.",
  efforts: "Efforts on the register, archived ones not counted.",
  unowned:
    "Efforts not yet routed — not groomed, or groomed with no owner settled. This includes rows reading 'Suggested: …' — a suggestion is the requester's expectation and does not route the effort. Red because it is not recorded, not because it is late.",
  shared:
    "Efforts needed by more than one team (common scope or with +1 teams) — the build-once opportunities.",
  committed:
    "Efforts a platform team has committed to deliver this cycle (vs. backlog). Set by an admin with Groom… in the effort.",
  yours:
    "Efforts you registered, or that name you as point of contact, and how many of them are groomed. Click to show only yours.",
};

export const FILTER_HELP = {
  shared: "Only efforts needed by more than one team (build-once candidates).",
  unowned: STAT_HELP.unowned,
  committed: "Only efforts a platform team has committed to this cycle.",
  archived: "Include archived efforts (hidden by default).",
  mine: "Only efforts you registered, or that name you as point of contact.",
  search: "Narrow the queue to efforts whose name contains this text.",
  team: "Only efforts registered by this team.",
  product: "Only efforts for this product.",
  owner: "Only efforts this team is expected to deliver — routed, or suggested by the requester.",
  status: "Only efforts at this platform status.",
  phase: "Only efforts targeting this roadmap phase (the platform's, else the one requested).",
};

export const SCORE_HELP =
  "Priority score = value × reach ÷ effort. Value from the priority set at grooming, reach = teams served (shared ranks higher), effort = platform cost. Admin-set effort refines it.";

/* the three ⓘ panels under the queue's intro (QH4), closed until asked for.
   Each paraphrases a decision, cited beside it. */
export interface QueueConcept {
  id: "owners" | "score" | "outcome";
  title: string;
  body: readonly string[];
}

export const QUEUE_CONCEPTS: readonly QueueConcept[] = [
  {
    /* intake-unified-form.md UF6, intake-ownership-model.md OM2 */
    id: "owners",
    title: "Who owns an effort",
    body: [
      "The Owners column names the team(s) expected to deliver the effort, in one of three states.",
      "A plain team name: an intake manager has groomed the effort and named its owner. Only this routes it.",
      "Suggested (orange): the team the requester expects to deliver it. It stays a suggestion until an intake manager grooms the effort, so these rows still count under Nobody owns yet.",
      "Nobody owns this yet (red): no owner is named. Red means not recorded, not late.",
    ],
  },
  {
    /* capabilities.ts computeScore — value × reach ÷ effort, M assumed */
    id: "score",
    title: "How the score works",
    body: [
      "Priority score = value × reach ÷ effort. Higher means do sooner.",
      "Value comes from the priority an intake manager sets when grooming (Must, Should, Could; neutral until set). Reach is how many teams it serves, so shared efforts rank higher. Effort is the platform cost (S, M, L, XL), set by an intake manager; until it is set, M is assumed.",
      "Scores rank efforts within one owning team, not across teams: two teams' efforts are worked by different engineers.",
    ],
  },
  {
    /* intake-project-mapping.md IPM9, intake-unified-form.md UF17–UF19 */
    id: "outcome",
    title: "What an outcome decides",
    body: [
      "Once an effort is groomed, an intake manager records its outcome under Disposition in the opened effort.",
      "It becomes a new project, is covered by or expands an existing project, is taken on by platform, is built by its team with a named platform reviewer, or is a duplicate of another effort.",
      "The outcome can be changed or cleared later, and every change is kept.",
    ],
  },
];

/* one line under each section of an opened effort (QH6): what it holds and
   who fills it in */
export const DRAWER_LEAD = {
  submission:
    "What the requesting team filed. Expected delivery owners are their suggestion until an intake manager grooms the effort.",
  tracking:
    "The platform's view of the effort: status, health, effort and the score it drives. An intake manager sets it with Groom….",
  disposition:
    "The outcome an intake manager records once the effort is groomed: a project, who builds it, or a duplicate.",
  delivery:
    "An effort isn't done at Accepted. An intake manager ticks each of the seven stages as it is reached.",
};

export const UNGROOMED_NOTE =
  "Not groomed yet. An intake manager sets its owner, status, effort and health with Groom….";

/* the Board view's grooming words (QH8, IG11): what grooming is for, the
   steps — owner and grooming in Groom… (intake-flow-review.md FR2), the
   outcome under Disposition — and one hover per lane (IG3) */
export const GROOM_HELP = {
  intro:
    "Walk the efforts at the intake sync and record the platform's view of each, so the queue is ordered and every effort has an owner and an outcome.",
  steps: [
    "Start with the Not groomed lane. Open a card: the effort opens over the board, and ↑ and ↓ step to the next card.",
    "Under Platform tracking, choose Groom… and set its priority (Must, Should, Could, Won't), its size (S to XL; until sized, M is assumed), its status, and its health once work is under way. Tick Committed if a platform team will deliver it this cycle.",
    "In the same Groom…, name its owner: confirm the requester's suggestion or change it. An effort is routed once it is groomed and owned.",
    "Decide its outcome under Disposition, once it is groomed.",
  ],
  /* IG6: a change to status, effort, health, committed or notes marks it groomed */
  saveNote: "Saving a change to status, effort, health, Committed or notes also marks the effort groomed.",
  lanes: {
    "Not groomed": "Efforts nobody has groomed yet, whatever their status. Start here.",
    Triage: "Groomed efforts still at status Triage, waiting for a platform decision.",
    Accepted: "Efforts the platform has accepted, not started yet.",
    "In progress": "Efforts with work under way.",
    "On hold / Blocked": "Efforts whose work is paused or blocked. Say why in the platform notes.",
    Delivered: "Efforts the platform has delivered.",
    Closed: "Efforts rejected, or closed as a duplicate of another effort.",
  } as Readonly<Record<string, string>>,
};

/* what choosing each outcome records (QH7) — no more than UF17–UF19 and
   intake-project-mapping.md say */
type DispositionValue = (typeof DISPOSITIONS)[number]["value"];
export const DISPOSITION_HELP: Readonly<Record<DispositionValue, string>> = {
  new_project: "Creates a new draft project, named here, and links this effort to it.",
  covered: "An existing project already covers this effort. Links it to that project, to watch.",
  expand_scope: "This effort widens an existing project's scope. Links it to that project.",
  platform_takes_on:
    "Another team needs it too, so platform builds it once. Links it to the project doing the work.",
  team_builds_platform_reviews:
    "The requesting team builds it and a named platform reviewer takes part in its designs and reviews. Links it to the project doing the work.",
  duplicate_of:
    "The same as another effort. This team's +1 moves onto that effort, and this one is archived with the link kept.",
};

/* ── CSV export (UP6): the prototype's column order, minus what the board
   reads cannot answer (submitter email, comment count, origin submitter —
   the server-composed export is the named follow-up) and minus the
   per-domain columns the cut removed (UF5) ── */

const yesNo = (b: boolean): string => (b ? "Yes" : "No");
const done = (b: boolean | undefined): string => (b ? "Done" : "");

export const CSV_COLUMNS: ReadonlyArray<[string, (r: BoardView) => string | number]> = [
  ["Market", (r) => r.market ?? ""],
  ["Team", (r) => r.teamName],
  ["Effort", (r) => r.name],
  ["Product", (r) => r.productName ?? ""],
  ["Point of contact", (r) => r.pointOfContact?.name ?? ""],
  ["Description", (r) => r.description ?? ""],
  ["Entered by", (r) => r.recorded.by?.name ?? r.recorded.source],
  ["Scope", (r) => labelFor(SCOPES, r.scope)],
  ["Priority", (r) => labelFor(PRIORITIES, r.priority)],
  ["Submitter status", (r) => labelFor(SUB_STATUSES, r.subStatus)],
  ["Needed by", (r) => r.neededBy ?? ""],
  ["Requested phase", (r) => labelFor(PHASE_TARGETS, r.phaseTarget)],
  ["Roadmap phase", (r) => labelFor(PHASE_TARGETS, r.tracking?.roadmapPhase)],
  ["Platform status", (r) => labelFor(PLATFORM_STATUSES, r.tracking?.platformStatus)],
  ["Health", (r) => labelFor(HEALTHS, r.tracking?.health)],
  ["Effort", (r) => labelFor(EFFORTS, r.tracking?.effort)],
  ["Score", (r) => r.score],
  ["Committed", (r) => yesNo(r.tracking?.committed ?? false)],
  ["Groomed", (r) => yesNo(r.tracking?.groomed ?? false)],
  ["Platform owner", (r) => r.tracking?.platformOwner ?? ""],
  ["Requesting team owner", (r) => r.tracking?.teamOwner ?? ""],
  ["Planned start", (r) => r.tracking?.plannedStart ?? ""],
  ["Planned end", (r) => r.tracking?.plannedEnd ?? ""],
  ["Jira epic", (r) => r.tracking?.jiraEpic ?? ""],
  [`Delivery (of ${DELIVERY_TOTAL})`, (r) => r.deliveryDone],
  ["Charter", (r) => done(r.tracking?.dcCharter)],
  ["Charter link", (r) => r.tracking?.charterLink ?? ""],
  ["Req drafted", (r) => done(r.tracking?.dcReqDrafted)],
  ["Jama linked", (r) => done(r.tracking?.dcReqLinked)],
  ["Code", (r) => done(r.tracking?.dcCode)],
  ["Test cases", (r) => done(r.tracking?.dcTestCases)],
  ["Test artifacts", (r) => done(r.tracking?.dcTestArtifacts)],
  ["Verified", (r) => done(r.tracking?.dcVerified)],
  ["Jama status", (r) => labelFor(JAMA_STATUSES, r.jamaStatus)],
  ["Jama link", (r) => r.jamaLink ?? ""],
  ["Dependencies / needs", (r) => r.dependencies ?? ""],
  /* blank means not recorded — an export never invents an owner (OM2). The
     team NAMES, not ids, so a spreadsheet reader needs no lookup table. */
  ["Expected delivery owners", (r) => ownersText(r.ownerNames)],
  ["Routed", (r) => yesNo(isRouted(r))],
  ["Platform notes", (r) => r.tracking?.platformNotes ?? ""],
  ["Stakeholders", (r) => r.stakeholderCount],
  ["Archived", (r) => yesNo(r.archived)],
  ["Recorded", (r) => r.recorded.date],
];

const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const csvFor = (rows: BoardView[]): string =>
  [
    CSV_COLUMNS.map(([h]) => csvCell(h)).join(","),
    ...rows.map((r) => CSV_COLUMNS.map(([, get]) => csvCell(get(r))).join(",")),
  ].join("\n") + "\n";

/* ── exec rollup (UP8): by-market summaries over the same rows ── */

export interface RollupRow {
  /* the group's name — a market */
  market: string;
  total: number;
  green: number;
  amber: number;
  red: number;
  unset: number;
  committed: number;
  demoCritical: number;
  /* 0..1 over EVERY row in the group — null only when the group is empty */
  avgDelivery: number | null;
}

export function summarize(market: string, rows: BoardView[]): RollupRow {
  const health = (h: string) => rows.filter((r) => r.tracking?.health === h).length;
  return {
    market,
    total: rows.length,
    green: health("green"),
    amber: health("amber"),
    red: health("red"),
    unset: rows.filter((r) => (r.tracking?.health ?? null) === null).length,
    committed: rows.filter((r) => r.tracking?.committed ?? false).length,
    demoCritical: rows.filter((r) => effectivePhase(r) === "p3").length,
    avgDelivery: rows.length
      ? rows.reduce((n, r) => n + r.deliveryDone / DELIVERY_TOTAL, 0) / rows.length
      : null,
  };
}

export function rollupByMarket(rows: BoardView[]): RollupRow[] {
  const byMarket = new Map<string, BoardView[]>();
  for (const r of rows) {
    /* a platform team's own effort has no market; anything else without
       one is a gap, and says so */
    const m = r.market ?? (r.teamKind === "platform" ? "Platform" : "Market not recorded");
    if (!byMarket.has(m)) byMarket.set(m, []);
    byMarket.get(m)!.push(r);
  }
  return [...byMarket.entries()]
    .map(([m, rs]) => summarize(m, rs))
    .sort((a, b) => b.total - a.total || a.market.localeCompare(b.market));
}
